import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function AuthCallbackPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handle = async () => {
      try {
        // 1) Se já existe sessão, ótimo
        let { data: { session } } = await supabase.auth.getSession();

        // 2) Se não existe, tente extrair do fragmento (#access_token/#refresh_token)
        if (!session) {
          const hash = (window.location.hash || '').replace(/^#/, '');
          const h = new URLSearchParams(hash);
          const access_token = h.get('access_token');
          const refresh_token = h.get('refresh_token');

          if (access_token && refresh_token) {
            const { data, error } = await supabase.auth.setSession({
              access_token,
              refresh_token,
            });
            if (error) throw error;
            session = data.session ?? null;
          }
        }

        if (!session) {
          throw new Error('Não foi possível criar a sessão a partir do link.');
        }

        // 3) Se é primeiro acesso, força definir senha
        const { data: profile } = await supabase
          .from('users')
          .select('must_set_password')
          .eq('id', session.user.id)
          .single();

        if (profile?.must_set_password) {
          navigate('/set-password', { replace: true });
          return;
        }

        // 4) Tudo certo → home
        setSuccess(true);
        setTimeout(() => navigate('/', { replace: true }), 800);
      } catch (e: any) {
        console.error('Erro no callback:', e);
        setError(e?.message ?? 'Falha ao processar o link.');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      } finally {
        setLoading(false);
      }
    };

    handle();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray via-white to-light-gray-alt dark:from-soft-black dark:via-deep-purple/20 dark:to-soft-black font-exo flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-darling-lilac via-transparent to-ocean-water" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-darling-lilac/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-ocean-water/20 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative text-center"
      >
        {loading && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-darling-lilac/10 dark:bg-darling-lilac/20 mb-6">
              <Loader2 className="h-8 w-8 text-darling-lilac animate-spin" />
            </div>
            <h1 className="text-2xl font-exo font-bold text-soft-black dark:text-white mb-2">
              Processando convite...
            </h1>
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
              Aguarde enquanto validamos seu acesso
            </p>
          </>
        )}

        {success && !loading && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-ocean-water/10 dark:bg-ocean-water/20 mb-6">
              <CheckCircle className="h-8 w-8 text-ocean-water" />
            </div>
            <h1 className="text-2xl font-exo font-bold text-soft-black dark:text-white mb-2">
              Bem-vindo(a)!
            </h1>
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
              Convite aceito com sucesso. Redirecionando...
            </p>
          </>
        )}

        {error && !loading && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/10 dark:bg-red-500/20 mb-6">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-exo font-bold text-soft-black dark:text-white mb-2">
              Erro na autenticação
            </h1>
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo mb-4">
              {error}
            </p>
            <p className="text-sm text-soft-black/50 dark:text-light-gray/60 font-exo">
              Redirecionando para o login...
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}